<template>
  <div class="admin-layout">
    <aside class="admin-sidebar">
      <h2 class="admin-title">Admin</h2>

      <nav class="admin-nav">
        <router-link
          v-for="item in menuItems"
          :key="item.to"
          :to="item.to"
          class="admin-nav-link"
          :class="{ active: $route.path === item.to }"
        >
          <span class="admin-nav-icon" v-html="item.icon"></span>
          <span>{{ item.label }}</span>
        </router-link>
      </nav>
    </aside>

    <main class="admin-main">
      <router-view />
    </main>
  </div>
</template>

<script>
import { computed } from "vue";
import { isSuperAdmin, isOrganizer } from "@/utils/auth";

const ICONS = {
  events:
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>',
  concerts:
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>',
  users:
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>',
  analytics:
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>',
};

export default {
  name: "AdminLayout",
  setup() {
    const menuItems = computed(() => {
      const items = [];

      // "Mina event" är organizer:s vy för events kopplade till deras egen
      // venue. Super-admin har ingen venue och hanterar alla external events
      // via "Externa event" istället - så vi visar inte länken för dem.
      if (isOrganizer.value) {
        items.push({
          to: "/admin/my-events",
          label: "Mina event",
          icon: ICONS.events,
        });
      }

      if (isSuperAdmin.value) {
        items.push({
          to: "/admin/concerts",
          label: "Alla konserter",
          icon: ICONS.concerts,
        });
        items.push({
          to: "/admin/external-events",
          label: "Externa event",
          icon: ICONS.events,
        });
        items.push({
          to: "/admin/users",
          label: "Användare",
          icon: ICONS.users,
        });
        items.push({
          to: "/admin/analytics",
          label: "Analytics",
          icon: ICONS.analytics,
        });
      }

      return items;
    });

    return { menuItems };
  },
};
</script>

<style scoped>
.admin-layout {
  display: grid;
  grid-template-columns: 240px 1fr;
  min-height: calc(100vh - 84px);
}

.admin-sidebar {
  background: var(--color-bg);
  border-right: 1px solid var(--color-border);
  padding: 2rem 1rem;
}

.admin-title {
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
  margin: 0 0 1.5rem 0.75rem;
  font-weight: 700;
}

.admin-nav {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.admin-nav-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.7rem 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text-secondary);
  text-decoration: none;
  transition: background-color 0.12s ease, color 0.12s ease;
}

.admin-nav-link:hover {
  background: var(--color-border-light, rgba(0, 0, 0, 0.04));
  color: var(--color-text);
}

.admin-nav-link.active {
  background: var(--color-accent);
  color: #fff;
}

.admin-nav-link.active:hover {
  background: var(--color-accent);
  color: #fff;
}

.admin-nav-icon {
  display: flex;
  align-items: center;
}

.admin-main {
  padding: 2rem;
  background: var(--color-bg-soft, transparent);
  overflow-x: auto;
}

@media (max-width: 800px) {
  .admin-layout {
    grid-template-columns: 1fr;
  }
  .admin-sidebar {
    border-right: none;
    border-bottom: 1px solid var(--color-border);
    padding: 1rem;
  }
  .admin-nav {
    flex-direction: row;
    overflow-x: auto;
  }
  .admin-title {
    display: none;
  }
}
</style>
